import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Loader2, Camera, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { resolveAssetUrl } from '../lib/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewDialog({
  open, onOpenChange,
  // Listing-review props (default mode)
  listingId, listingTitle,
  // Buyer-review props (seller reviewing a buyer)
  mode = 'listing',
  orderId, buyerId, buyerName,
  onSubmitted,
}) {
  const { language } = useLanguage();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(null);
  const [checking, setChecking] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const MAX_IMAGES = 5;
  const allowImages = mode === 'listing';

  // Reset + check if user already reviewed (listing by buyer / buyer by seller).
  useEffect(() => {
    if (!open) return;
    setRating(0);
    setHover(0);
    setComment('');
    setImages([]);
    setExisting(null);
    const url = mode === 'buyer'
      ? (orderId ? `${API}/buyer-reviews/mine/${orderId}` : null)
      : (listingId ? `${API}/reviews/mine/${listingId}` : null);
    if (!url) return;
    setChecking(true);
    axios.get(url)
      .then(({ data }) => { if (data && data.exists) setExisting(data); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [open, listingId, orderId, mode]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) {
      toast.error(language === 'lv' ? `Maks. ${MAX_IMAGES} attēli` : `Max ${MAX_IMAGES} images`);
      return;
    }
    const toUpload = files.slice(0, room);
    setUploading(true);
    const newUrls = [];
    for (const f of toUpload) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(language === 'lv' ? `${f.name}: pārāk liels (maks. 5MB)` : `${f.name}: too large (max 5MB)`);
        continue;
      }
      const form = new FormData();
      form.append('file', f);
      try {
        const { data } = await axios.post(`${API}/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data && data.url) newUrls.push(data.url);
      } catch (err) {
        toast.error(language === 'lv' ? 'Attēla augšupielāde neizdevās' : 'Image upload failed');
      }
    }
    setImages((prev) => [...prev, ...newUrls]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error(language === 'lv' ? 'Lūdzu izvēlies vērtējumu' : 'Please select a rating');
      return;
    }
    if (comment.trim().length < 3) {
      toast.error(language === 'lv' ? 'Komentārs pārāk īss (vismaz 3 simboli)' : 'Comment too short (min 3 chars)');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'buyer') {
        await axios.post(`${API}/buyer-reviews`, {
          order_id: orderId,
          buyer_id: buyerId,
          rating,
          comment: comment.trim(),
        });
      } else {
        await axios.post(`${API}/reviews`, {
          listing_id: listingId,
          rating,
          comment: comment.trim(),
          images,
        });
      }
      toast.success(language === 'lv' ? 'Paldies par atsauksmi!' : 'Thanks for your review!');
      if (onSubmitted) onSubmitted();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (language === 'lv' ? 'Kļūda' : 'Error'));
    } finally {
      setLoading(false);
    }
  };

  const dialogTitle = mode === 'buyer'
    ? (language === 'lv' ? 'Novērtēt pircēju' : 'Rate the buyer')
    : (language === 'lv' ? 'Atstāt atsauksmi' : 'Leave a review');
  const dialogSubject = mode === 'buyer' ? buyerName : listingTitle;
  const placeholder = mode === 'buyer'
    ? (language === 'lv' ? 'Kā gāja darījums ar šo pircēju? (maksāšana, saziņa...)' : 'How did it go with this buyer? (payment, communication...)')
    : (language === 'lv' ? 'Pastāsti citiem pircējiem par savu pieredzi...' : 'Tell other buyers about your experience...');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit">
            {dialogTitle}
          </DialogTitle>
          <DialogDescription className="line-clamp-2">
            {dialogSubject}
          </DialogDescription>
        </DialogHeader>

        {checking ? (
          <div className="py-10 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#D97736]" />
          </div>
        ) : existing ? (
          <div className="py-6 text-center">
            <div className="flex justify-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  className={`w-6 h-6 ${n <= existing.rating ? 'fill-[#D97736] text-[#D97736]' : 'text-[#E5E4E0]'}`}
                />
              ))}
            </div>
            <p className="text-sm text-[#5C5C5C] mb-4">
              {language === 'lv' ? 'Tu jau esi atstājis atsauksmi šim sludinājumam:' : "You've already reviewed this listing:"}
            </p>
            <p className="text-sm italic text-[#121212] bg-[#F3F2EE] p-3 rounded-lg">
              "{existing.comment}"
            </p>
            {Array.isArray(existing.images) && existing.images.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center mt-3">
                {existing.images.map((url) => (
                  <img key={url} src={resolveAssetUrl(url)} alt="" className="w-16 h-16 object-cover rounded-lg border border-[#E5E4E0]" />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#121212] block mb-2">
                {language === 'lv' ? 'Vērtējums' : 'Rating'}
              </label>
              <div className="flex gap-1" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`star-${n}`}
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        n <= (hover || rating)
                          ? 'fill-[#D97736] text-[#D97736]'
                          : 'text-[#E5E4E0]'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#121212] block mb-2">
                {language === 'lv' ? 'Komentārs' : 'Comment'}
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={placeholder}
                rows={4}
                maxLength={500}
                className="border-[#E5E4E0] focus:border-[#D97736] focus:ring-[#D97736]"
                data-testid="review-comment"
              />
              <p className="text-xs text-[#5C5C5C] mt-1 text-right">{comment.length}/500</p>
            </div>

            {allowImages && (
              <div>
                <label className="text-sm font-medium text-[#121212] block mb-2">
                  {language === 'lv' ? 'Attēli (neobligāti)' : 'Photos (optional)'}
                  <span className="text-xs text-[#5C5C5C] font-normal ml-2">
                    {images.length}/{MAX_IMAGES}
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {images.map((url, i) => (
                    <div key={url} className="relative group">
                      <img src={resolveAssetUrl(url)} alt="" className="w-20 h-20 object-cover rounded-lg border border-[#E5E4E0]" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {images.length < MAX_IMAGES && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-20 h-20 border-2 border-dashed border-[#E5E4E0] rounded-lg flex flex-col items-center justify-center text-[#5C5C5C] hover:border-[#D97736] hover:text-[#D97736] transition-colors disabled:opacity-50"
                      data-testid="upload-review-image"
                    >
                      {uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Camera className="w-5 h-5 mb-1" />
                          <span className="text-[10px]">
                            {language === 'lv' ? 'Pievienot' : 'Add'}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUpload}
                  className="hidden"
                />
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
                className="border-[#E5E4E0]"
              >
                {language === 'lv' ? 'Atcelt' : 'Cancel'}
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid="review-submit"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {language === 'lv' ? 'Publicēt' : 'Publish'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
